import { Head, Link } from "@inertiajs/react"
import { Briefcase, Building2, Link2, UserCircle, UserPlus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { AdminInvitesDigestCard } from "@/components/admin/admin-invites-digest-card"
import { InviteAdminDialog } from "@/components/admin/invite-admin-dialog"
import { StatCard } from "@/components/admin/stat-card"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { nameOrDash } from "@/lib/admin-placeholder"
import { toAppLocale } from "@/lib/i18n/locales"
import { formatRelativeIntl } from "@/lib/relative-time"
import { adminAgenciesPath, adminBrandsPath, adminCreatorsPath } from "@/routes"
import type { AdminOverviewProps } from "@/types/admin"

/**
 * Admin/Overview — `/admin` landing page (OKS-15).
 *
 * Replaces the OKS-7 placeholder card grid (`Admin/Home.tsx`) per
 * Frontend TRD v6 §Pages / Routes. Layout per UX Spec §2 + PRD §6:
 *
 *   - 4 stat cards in a responsive grid (Brands / Agencies / Creators /
 *     Active memberships) with icon + label + count
 *   - 3 "Last 7 days" recent-activity cards side-by-side on desktop,
 *     stacked on mobile (Backend hard-caps each list at 5 items via
 *     `RECENT_LIST_SIZE`)
 *   - No charts in v1
 *
 * The page is read-only — no mutations, no toolbar, no pagination. As
 * such it doesn't consume `<ListShell>` / `<ListToolbar>` /
 * `<ListPagination>`; the four LIST pages (Brands / Agencies / Creators
 * / Blocklist) are the primary consumers of those primitives.
 *
 * Test IDs per the v6 contract: `admin-overview` (page region),
 * `admin-overview-count-{brands|agencies|creators|memberships}`,
 * `admin-overview-recent-{brands|agencies|creators}-list`. Empty-state
 * carries `data-testid="admin-overview-recent-{tenants}-empty"` so QA
 * can assert the empty-message branch separately from the populated one.
 *
 * Backend's `Admin::OverviewController` hard-caps the recent lists to 5
 * items per `RECENT_WINDOW = 7.days` and returns ISO-8601 strings for
 * the timestamps — FE renders relative-time via `formatRelativeIntl`
 * matching the OKS-11 `sent_at` row pattern.
 */
export default function AdminOverview({
  counts,
  recent,
  can_invite_admin,
  pending_admin_invites,
  total_pending_admin_invites_count,
}: AdminOverviewProps) {
  const { t, i18n } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("admin.overview.title")} />
      <div
        className="flex flex-col gap-6 p-6"
        data-testid="admin-overview"
        role="region"
        aria-label={t("admin.overview.title")}
      >
        <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("admin.overview.title")}
          </h1>
          {/*
           * OKS-12 Path A — header-right "Invite admin" affordance.
           * Server-driven `can_invite_admin` gate is always true for OKS
           * Admin in v1 but threaded through so member-tier and other
           * future viewers don't see the button at all (per UX a11y —
           * controls absent from DOM, not show-and-disable).
           */}
          {can_invite_admin && (
            <InviteAdminDialog
              trigger={
                <Button type="button" data-testid="admin-invite-admin-button">
                  <UserPlus className="size-4" aria-hidden="true" />
                  {t("admin.invites.dialog.invite_admin.trigger")}
                </Button>
              }
            />
          )}
        </header>

        <section
          aria-label={t("admin.overview.title")}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          <StatCard
            testId="admin-overview-count-brands"
            icon={Building2}
            label={t("admin.overview.counts.brands")}
            value={counts.brands}
          />
          <StatCard
            testId="admin-overview-count-agencies"
            icon={Briefcase}
            label={t("admin.overview.counts.agencies")}
            value={counts.agencies}
          />
          <StatCard
            testId="admin-overview-count-creators"
            icon={UserCircle}
            label={t("admin.overview.counts.creators")}
            value={counts.creators}
          />
          <StatCard
            testId="admin-overview-count-memberships"
            icon={Link2}
            label={t("admin.overview.counts.active_memberships")}
            value={counts.active_memberships}
          />
        </section>

        <section
          aria-label={t("admin.overview.recent.section_title")}
          className="grid gap-4 lg:grid-cols-3"
        >
          <RecentList
            testId="admin-overview-recent-brands-list"
            title={t("admin.overview.recent.brands_title")}
            emptyText={t("admin.overview.recent.empty_brands")}
            viewAllLabel={t("admin.overview.recent.view_all_brands")}
            viewAllHref={adminBrandsPath()}
            items={recent.brands.map((b) => ({
              key: `brand-${b.id}`,
              primary: b.name,
              secondary: formatRelativeIntl(
                b.created_at,
                toAppLocale(i18n.language),
              ),
            }))}
          />
          <RecentList
            testId="admin-overview-recent-agencies-list"
            title={t("admin.overview.recent.agencies_title")}
            emptyText={t("admin.overview.recent.empty_agencies")}
            viewAllLabel={t("admin.overview.recent.view_all_agencies")}
            viewAllHref={adminAgenciesPath()}
            items={recent.agencies.map((a) => ({
              key: `agency-${a.id}`,
              primary: a.name,
              secondary: formatRelativeIntl(
                a.created_at,
                toAppLocale(i18n.language),
              ),
            }))}
          />
          <RecentList
            testId="admin-overview-recent-creators-list"
            title={t("admin.overview.recent.creator_joins_title")}
            emptyText={t("admin.overview.recent.empty_creator_joins")}
            viewAllLabel={t("admin.overview.recent.view_all_creators")}
            viewAllHref={adminCreatorsPath()}
            items={recent.creator_joins.map((j) => ({
              key: `join-${j.user.id}-${j.brand.id}`,
              icon: UserPlus,
              primary: nameOrDash(j.user.name),
              secondary: t("admin.overview.recent.joined_at_brand", {
                brand: nameOrDash(j.brand.name),
              }),
              tertiary: formatRelativeIntl(
                j.joined_at,
                toAppLocale(i18n.language),
              ),
            }))}
          />
        </section>

        {/*
         * OKS-12 — pending OKS-Admin invites digest. Sourced from
         * `Invitation.pending` (≤5, expired excluded). Lives below the
         * 7-day digest section so admin can scan totals → recent creator
         * joins → outstanding admin invites without scrolling.
         */}
        <section
          aria-label={t("admin.invites.digest.title")}
          data-testid="admin-overview-pending-admins"
        >
          <AdminInvitesDigestCard
            invites={pending_admin_invites}
            total={total_pending_admin_invites_count}
          />
        </section>
      </div>
    </AppLayout>
  )
}

interface RecentItem {
  key: string
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: "true" }>
  primary: string
  secondary?: string
  tertiary?: string
}

interface RecentListProps {
  testId: string
  title: string
  emptyText: string
  viewAllLabel: string
  viewAllHref: string
  items: RecentItem[]
}

function RecentList({
  testId,
  title,
  emptyText,
  viewAllLabel,
  viewAllHref,
  items,
}: RecentListProps) {
  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="text-base font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {items.length === 0 ? (
          <p
            className="text-muted-foreground text-sm"
            data-testid={`${testId}-empty`}
          >
            {emptyText}
          </p>
        ) : (
          <ul className="flex flex-col gap-2.5" role="list">
            {items.map((item) => (
              <li
                key={item.key}
                className="flex items-start gap-2.5"
                role="listitem"
              >
                {item.icon && (
                  <item.icon
                    className="text-muted-foreground mt-0.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                )}
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-foreground truncate text-sm font-medium">
                    {item.primary}
                  </span>
                  {item.secondary && (
                    <span className="text-muted-foreground truncate text-xs">
                      {item.secondary}
                      {item.tertiary && (
                        <>
                          {" · "}
                          <span>{item.tertiary}</span>
                        </>
                      )}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        <Link
          href={viewAllHref}
          className="text-foreground hover:text-foreground/80 mt-auto text-xs font-medium underline-offset-4 hover:underline"
        >
          {viewAllLabel}
        </Link>
      </CardContent>
    </Card>
  )
}
